import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import usePageTitle from "../hooks/usePageTitle";
import { hasAboutContent, AboutChurchSection } from "./public/AboutChurchSection";
import { AnnouncementsSection } from "./public/AnnouncementsSection";
import { BirthdaysSection } from "./public/BirthdaysSection";
import { LEGACY_BRAND, genericBrand, SiteBrandContext, shortChurchName, resolveAddress } from "./public/brand";
import { DEFAULT_INFO, ministryIconFor, GENERIC_INFO } from "./public/content";
import { GallerySection } from "./public/GallerySection";
import { Hero } from "./public/Hero";
import { MessageIcon } from "./public/icons";
import { LeadershipSection } from "./public/LeadershipSection";
import { LocationContactsSection } from "./public/LocationContactsSection";
import { MinistriesSection } from "./public/MinistriesSection";
import { NextServicesSection } from "./public/NextServicesSection";
import { PublicFooter } from "./public/PublicFooter";
import { PublicHeader } from "./public/PublicHeader";
import {
  isRecentAnnouncement,
  buildEndpoint,
  toAbsoluteUrl,
  mapsDirectionsUrl,
  toGoogleEmbedUrl,
  normalizeVerse,
  formatMzPhone,
  toWhatsAppHref,
  prayerHref,
  isLikelyBrokenUploadUrl,
  parseLocalDate,
  habitualScheduleLines,
} from "./public/utils";

/**
 * Site público. Em "/" mostra a igreja original; em "/igreja/:churchSlug"
 * mostra a igreja indicada (a API recebe o slug no cabeçalho X-Church-Slug).
 */
export default function PublicHome() {
  const { churchSlug } = useParams();
  const slug = String(churchSlug || "").trim().toLowerCase();
  const [notFound, setNotFound] = useState(false);
  const [churchMeta, setChurchMeta] = useState(null);
  const [services, setServices] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [media, setMedia] = useState([]);
  const [heroMedia, setHeroMedia] = useState([]);
  const [shareImage, setShareImage] = useState("");
  const [heroCover, setHeroCover] = useState(null);
  // Sem slug é sempre a igreja original; com slug só o sabemos quando /info responde.
  const [siteInfo, setSiteInfo] = useState(() =>
    slug ? { ...GENERIC_INFO } : { ...DEFAULT_INFO, scheduleHours: [] }
  );
  const [loadingPublic, setLoadingPublic] = useState(true);

  const brand = useMemo(() => {
    const isDefault = churchMeta ? Boolean(churchMeta.isDefault) : !slug;
    const base = isDefault ? LEGACY_BRAND : genericBrand(churchMeta?.shortName || churchMeta?.name || "");
    return { ...base, logoUrl: churchMeta?.logoUrl || "", primaryColor: churchMeta?.primaryColor || "" };
  }, [churchMeta, slug]);
  const hasAbout = hasAboutContent(siteInfo.about);
  const brandName = shortChurchName(siteInfo?.churchName, brand);
  const pageTitle = brandName || "Igreja";
  usePageTitle(pageTitle, { suffix: brand.isDefault ? "IEAD-HB" : "" });

  useEffect(() => {
    let active = true;
    const publicGet = (segment, config = {}) =>
      axios.get(buildEndpoint(segment), {
        ...config,
        headers: slug ? { "X-Church-Slug": slug } : undefined,
      });
    (async () => {
      try {
        const [infoRes, svcRes, annRes, birthdayRes, mediaRes, heroMediaRes, shareImageRes] = await Promise.allSettled([
          publicGet("/info"),
          publicGet("/services"),
          publicGet("/announcements"),
          publicGet("/birthdays", { params: { days: 7 } }),
          publicGet("/media", { params: { limit: 12, placement: "gallery" } }),
          publicGet("/media", { params: { limit: 6, placement: "carousel" } }),
          publicGet("/share-image"),
        ]);

        if (!active) return;

        if (infoRes.status === "rejected" && infoRes.reason?.response?.status === 404) {
          setNotFound(true);
          return;
        }

        if (infoRes.status === "fulfilled" && infoRes.value?.data) {
          const data = infoRes.value.data;
          setChurchMeta(data.church || null);
          setSiteInfo((prev) => ({
            ...prev,
            ...data,
            contacts: { ...prev.contacts, ...(data.contacts || {}) },
            ctas: {
              ...prev.ctas,
              ...(data.ctas || {}),
              social: { ...prev.ctas?.social, ...(data.ctas?.social || {}) },
            },
            ministries:
              Array.isArray(data.ministries) && data.ministries.length
                ? data.ministries
                : prev.ministries,
            leadership:
              Array.isArray(data.leadership) && data.leadership.length
                ? data.leadership
                : prev.leadership,
            scheduleHours: Array.isArray(data.scheduleHours) ? data.scheduleHours : [],
          }));
          if (data.heroImageUrl && !isLikelyBrokenUploadUrl(data.heroImageUrl)) {
            setHeroCover(data.heroImageUrl);
          }
        }

        if (svcRes.status === "fulfilled" && Array.isArray(svcRes.value.data) && svcRes.value.data.length) {
          setServices(svcRes.value.data);
        }

        if (annRes.status === "fulfilled" && Array.isArray(annRes.value.data)) {
          setAnnouncements(
            annRes.value.data.map((a, idx) => ({
              id: a.id || idx,
              title: a.title,
              description: a.message,
              category: a.category || a.type || "Comunicado",
              date: a.publish_at || a.created_at,
            })).filter((a) => isRecentAnnouncement(a.date))
          );
        }

        if (birthdayRes.status === "fulfilled" && Array.isArray(birthdayRes.value.data)) {
          setBirthdays(birthdayRes.value.data);
        }

        if (mediaRes.status === "fulfilled" && Array.isArray(mediaRes.value.data)) {
          setMedia(
            mediaRes.value.data.filter((m) => m?.url && !isLikelyBrokenUploadUrl(m.url))
          );
        }

        if (heroMediaRes.status === "fulfilled" && Array.isArray(heroMediaRes.value.data)) {
          setHeroMedia(heroMediaRes.value.data.filter((m) => m?.url && !isLikelyBrokenUploadUrl(m.url)));
        }

        if (shareImageRes.status === "fulfilled" && shareImageRes.value?.data?.url) {
          setShareImage(shareImageRes.value.data.url);
        }
      } catch {
        // mantém defaults
      } finally {
        if (active) setLoadingPublic(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  const sitePath = slug ? `/igreja/${slug}` : "/";

  useEffect(() => {
    const desc =
      siteInfo.heroSubtitle ||
      siteInfo.tagline ||
      (brand.isDefault
        ? 'IEAD Hulene "B" — cultos, anúncios e contactos em Maputo.'
        : `${brandName} — cultos, anúncios e contactos.`);
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", pageTitle);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", desc);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    const pageUrl = typeof window !== "undefined" ? `${window.location.origin}${sitePath}` : "";
    if (pageUrl) {
      if (!ogUrl) {
        ogUrl = document.createElement("meta");
        ogUrl.setAttribute("property", "og:url");
        document.head.appendChild(ogUrl);
      }
      ogUrl.setAttribute("content", pageUrl);

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", pageUrl);
    }

    // og-share.jpg é a imagem da igreja original; as outras usam só as suas.
    const imageUrl =
      toAbsoluteUrl(shareImage || heroCover || siteInfo.heroImageUrl) ||
      (brand.isDefault ? toAbsoluteUrl("/og-share.jpg") || "https://www.ieadhuleneb.com/og-share.jpg" : "");
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (imageUrl) {
      if (!ogImage) {
        ogImage = document.createElement("meta");
        ogImage.setAttribute("property", "og:image");
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute("content", imageUrl);
      const ogImageSecure = document.querySelector('meta[property="og:image:secure_url"]');
      if (ogImageSecure) ogImageSecure.setAttribute("content", imageUrl);
      const twImage = document.querySelector('meta[name="twitter:image"]');
      if (twImage) twImage.setAttribute("content", imageUrl);
    }
  }, [siteInfo.heroSubtitle, siteInfo.tagline, siteInfo.heroImageUrl, pageTitle, heroCover, shareImage, brand, brandName, sitePath]);

  const sortedServices = useMemo(() => {
    return [...(services || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [services]);

  // A lista também traz cultos anteriores (para o separador «Anteriores»):
  // o próximo é o primeiro a partir de hoje, como na agenda.
  const nextService = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return sortedServices.find((s) => {
      const date = parseLocalDate(s.date);
      return date && date >= todayStart;
    });
  }, [sortedServices]);

  const address = resolveAddress(siteInfo.contacts?.address, brand);
  const phoneDisplay = formatMzPhone(siteInfo.contacts?.phone || siteInfo.contacts?.whatsapp);
  const whatsappDisplay = formatMzPhone(siteInfo.contacts?.whatsapp || siteInfo.contacts?.phone);
  const whatsappLink = toWhatsAppHref(
    siteInfo.contacts?.whatsapp || siteInfo.contacts?.phone,
    siteInfo.ctas?.whatsappLink
  );
  const prayerLink = prayerHref(siteInfo.ctas?.prayerUrl, whatsappLink);

  const heroData = useMemo(() => {
    const verseParts = normalizeVerse(siteInfo.heroVerse, siteInfo.heroVerseRef);
    const verseRef =
      verseParts.ref || (/minha casa|serviremos ao senhor/i.test(verseParts.text) ? "Josué 24:15" : "");
    const caption =
      siteInfo.heroCaption && siteInfo.heroCaption !== siteInfo.tagline
        ? siteInfo.heroCaption
        : "Um lugar para crescer na fé em família.";
    const carouselSlides = heroMedia.map((m) => m.url).filter(Boolean);
    // A imagem definida no campo Hero é a imagem principal. As imagens do
    // carrossel continuam disponíveis como slides seguintes e nunca podem
    // substituir silenciosamente o Hero configurado no admin.
    const primaryImage = heroCover || carouselSlides[0] || null;
    const additionalSlides = heroCover ? carouselSlides : carouselSlides.slice(1);
    return {
      image: primaryImage,
      slides: additionalSlides,
      brandName,
      title: siteInfo.heroTitle || (brand.isDefault ? "Firmar, Expandir e Discipular" : brandName),
      subtitle: siteInfo.heroSubtitle || "Louvor, oração e Palavra para toda a família.",
      tagline: siteInfo.tagline || (brand.isDefault ? "Comunidade viva em Maputo" : ""),
      verse: verseParts.text,
      verseRef,
      caption,
      address,
      phone: phoneDisplay,
      whatsappDisplay,
    };
  }, [heroCover, heroMedia, siteInfo, address, phoneDisplay, whatsappDisplay, brandName, brand]);

  const handleNav = useCallback((href) => {
    const id = href?.replace("#", "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const mapUrl = mapsDirectionsUrl(siteInfo.contacts?.directionsUrl, address);

  const contact = {
    brandName,
    address,
    phone: phoneDisplay,
    email: siteInfo.contacts?.email || "",
    whatsapp: whatsappDisplay,
    whatsappDisplay,
    whatsappLink,
    mapUrl,
    mapEmbed: siteInfo.contacts?.mapEmbed || siteInfo.contacts?.directionsUrl || "",
  };

  const resolvedMapEmbed = useMemo(() => {
    const directEmbed = toGoogleEmbedUrl(contact.mapEmbed);
    if (directEmbed) return directEmbed;
    return toGoogleEmbedUrl(contact.address);
  }, [contact.mapEmbed, contact.address]);

  const ministries = useMemo(() => {
    const list =
      Array.isArray(siteInfo.ministries) && siteInfo.ministries.length
        ? siteInfo.ministries
        : brand.isDefault
          ? DEFAULT_INFO.ministries
          : [];
    return list
      .filter((m) => String(m?.name || "").trim())
      .map((m, idx) => ({
        id: m.id || `min-${idx}`,
        name: m.name,
        blurb: m.blurb || "",
        icon: ministryIconFor(m.name, idx),
      }));
  }, [siteInfo.ministries, brand]);

  // Os horários escritos no Público (Admin); sem eles, os da agenda.
  const writtenHours = Array.isArray(siteInfo.scheduleHours)
    ? siteInfo.scheduleHours.filter((l) => String(l || "").trim())
    : [];
  const scheduleHours = writtenHours.length ? writtenHours : habitualScheduleLines(sortedServices);

  // Que secções existem (para o menu e o rodapé não levarem a secções vazias).
  // Enquanto carrega, conta com as que costumam existir.
  const sections = {
    about: hasAbout,
    leadership: (Array.isArray(siteInfo.leadership) && siteInfo.leadership.length > 0) || brand.isDefault,
    announcements: loadingPublic || announcements.length > 0,
    gallery: loadingPublic || media.length > 0,
    ministries: ministries.length > 0,
    birthdays: birthdays.length > 0,
  };

  // Dados estruturados (JSON-LD) para motores de pesquisa mostrarem
  // morada, telefone e redes sociais directamente nos resultados.
  useEffect(() => {
    const social = siteInfo.social || {};
    const sameAs = [social.facebook, social.instagram, social.youtube].filter(Boolean);
    const imageUrl = toAbsoluteUrl(shareImage || heroCover || siteInfo.heroImageUrl);
    const pageUrl = typeof window !== "undefined" ? `${window.location.origin}${sitePath}` : undefined;

    const data = {
      "@context": "https://schema.org",
      "@type": "Church",
      name: brandName,
      url: pageUrl,
      ...(imageUrl ? { image: imageUrl } : {}),
      ...(contact.address ? { address: { "@type": "PostalAddress", streetAddress: contact.address } } : {}),
      ...(contact.phone ? { telephone: contact.phone } : {}),
      ...(contact.email ? { email: contact.email } : {}),
      ...(sameAs.length ? { sameAs } : {}),
    };

    let script = document.querySelector('script[type="application/ld+json"][data-source="public-home"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-source", "public-home");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      script?.remove();
    };
  }, [brandName, contact.address, contact.phone, contact.email, siteInfo.social, shareImage, heroCover, siteInfo.heroImageUrl, sitePath]);

  if (notFound) {
    return (
      <div className="public-page min-h-screen flex items-center justify-center px-6 text-center text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-md">
          <h1 className="text-2xl font-extrabold">Igreja não encontrada</h1>
          <p className="mt-3 text-slate-600 dark:text-slate-400">
            O endereço que abriu não corresponde a nenhuma igreja activa. Confirme o link com a sua igreja.
          </p>
          <Link to="/" className="mt-6 inline-block font-semibold text-indigo-600 dark:text-indigo-300 hover:underline">
            Ir para a página inicial
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SiteBrandContext.Provider value={brand}>
    <div id="topo" className="public-page min-h-screen text-slate-900 dark:text-slate-100 bg-gradient-to-br from-slate-50 dark:from-slate-900 via-indigo-50/40 dark:via-indigo-950/40 to-sky-50 dark:to-sky-950/40 relative">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-32 h-[420px] w-[420px] rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute top-20 -right-36 h-[460px] w-[460px] rounded-full bg-sky-200/35 blur-3xl" />
        <div className="absolute bottom-[-180px] left-1/3 h-[520px] w-[520px] rounded-full bg-amber-200/25 blur-3xl" />
      </div>

      <PublicHeader
        onNav={handleNav}
        prayerLink={prayerLink}
        tagline={siteInfo.tagline}
        churchName={brandName}
        nextService={nextService}
        whatsapp={whatsappLink}
        sections={sections}
      />
      <Hero
        hero={heroData}
        nextService={nextService}
        whatsapp={whatsappLink}
        mapUrl={mapUrl}
        loading={loadingPublic}
        onScrollCultos={() => handleNav("#cultos")}
        ctas={{
          donateUrl: siteInfo.ctas?.donateUrl || "",
          streamUrl: siteInfo.ctas?.streamUrl || "",
        }}
      />

      <AboutChurchSection about={siteInfo.about} whatsapp={whatsappLink} onNav={handleNav} />

      <LeadershipSection leaders={siteInfo.leadership} />
      <NextServicesSection
        services={sortedServices}
        whatsapp={whatsappLink}
        loading={loadingPublic}
        calendarUrl={siteInfo.ctas?.calendarUrl || ""}
      />
      <BirthdaysSection birthdays={birthdays} loading={loadingPublic} />
      <AnnouncementsSection announcements={announcements} loading={loadingPublic} />
      <GallerySection media={media} loading={loadingPublic} />
      <MinistriesSection ministries={ministries} whatsapp={whatsappLink} />
      <LocationContactsSection
        contact={{ ...contact, mapEmbed: resolvedMapEmbed }}
        loading={loadingPublic}
        scheduleHours={scheduleHours}
      />
      <PublicFooter
        info={{
          churchName: brandName,
          contacts: { ...siteInfo.contacts, address, phone: phoneDisplay, email: siteInfo.contacts?.email || "" },
        }}
        social={siteInfo.ctas?.social || {}}
        onNav={handleNav}
        sections={sections}
      />

      {whatsappLink ? (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          aria-label="Falar no WhatsApp"
          className="sm:hidden fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white px-4 py-3 shadow-xl hover:bg-emerald-600 transition"
        >
          <MessageIcon className="w-5 h-5 text-white" />
          <span className="text-sm font-semibold">WhatsApp</span>
        </a>
      ) : null}
    </div>
    </SiteBrandContext.Provider>
  );
}
